/**
 * SchedulerFilterTabs
 * Horizontal tab row to filter tasks by status on the Task Scheduler page.
 * Each tab optionally shows a count badge pulled from the summary data.
 */

import styled, { css } from 'styled-components';
import type { TaskStatus } from '../../types/tasks';

// ============================================================================
// Types
// ============================================================================

type FilterKey = TaskStatus | 'ALL';

interface FilterTab {
  key: FilterKey;
  label: string;
}

export interface SchedulerFilterTabsProps {
  activeFilter: FilterKey;
  onFilterChange: (filter: FilterKey) => void;
  statusCounts?: Partial<Record<FilterKey, number>>;
}

// ============================================================================
// Constants
// ============================================================================

const TABS: FilterTab[] = [
  { key: 'ALL', label: 'All' },
  { key: 'PENDING', label: 'Pending' },
  { key: 'IN_PROGRESS', label: 'In Progress' },
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'OVERDUE', label: 'Overdue' },
];

// ============================================================================
// Styled components
// ============================================================================

const TabsWrapper = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.borders.light};
  overflow-x: auto;

  /* Hide scrollbar on overflow but allow scroll on mobile */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Tab = styled.button<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  background: none;
  border: none;
  cursor: pointer;
  white-space: nowrap;
  transition: color ${({ theme }) => theme.transitions.fast};
  position: relative;
  bottom: -2px;

  ${({ $isActive, theme }) =>
    $isActive
      ? css`
          color: ${theme.colors.primary.base};
          border-bottom: 2px solid ${theme.colors.primary.base};
        `
      : css`
          color: ${theme.colors.text.secondary};
          border-bottom: 2px solid transparent;

          &:hover {
            color: ${theme.colors.text.primary};
            border-bottom-color: ${theme.colors.borders.medium};
          }
        `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary.base};
    outline-offset: 2px;
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }
`;

const CountBadge = styled.span<{ $isActive: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  background: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.primary.lightSky : theme.colors.backgrounds.gray};
  color: ${({ $isActive, theme }) =>
    $isActive ? theme.colors.primary.base : theme.colors.text.secondary};
`;

// ============================================================================
// Component
// ============================================================================

export function SchedulerFilterTabs({
  activeFilter,
  onFilterChange,
  statusCounts,
}: SchedulerFilterTabsProps) {
  return (
    <TabsWrapper role="tablist" aria-label="Filter tasks by status">
      {TABS.map((tab) => {
        const isActive = tab.key === activeFilter;
        const count = statusCounts?.[tab.key];

        return (
          <Tab
            key={tab.key}
            $isActive={isActive}
            onClick={() => onFilterChange(tab.key)}
            role="tab"
            aria-selected={isActive}
            type="button"
          >
            {tab.label}
            {count !== undefined && (
              <CountBadge $isActive={isActive} aria-label={`${count} ${tab.label} tasks`}>
                {count}
              </CountBadge>
            )}
          </Tab>
        );
      })}
    </TabsWrapper>
  );
}

export default SchedulerFilterTabs;
